import React, { useEffect, useState } from "react";
import api, { formatApiError, asArray } from "../../lib/api";
import FileUpload from "./FileUpload";
import { Plus, Trash2, Edit3, X } from "lucide-react";
import { toast } from "sonner";

const inputCls = "w-full bg-transparent border-b border-black/15 focus:border-[#0060A7] outline-none py-2";
const textareaCls = "w-full bg-white border border-black/15 focus:border-[#0060A7] outline-none p-3 text-sm";

function Field({ label, children }) {
    return <div><label className="text-xs uppercase tracking-[0.2em] text-[#6B7280]">{label}</label><div className="mt-1">{children}</div></div>;
}

const blank = () => ({
    title_ar: "", title_en: "", slug: "",
    excerpt_ar: "", excerpt_en: "",
    body_ar: "", body_en: "",
    cover_image: "", category_ar: "", category_en: "",
    published_at: new Date().toISOString().slice(0, 10),
    published: true,
});

export default function AdminNews() {
    const [items, setItems] = useState([]);
    const [editing, setEditing] = useState(null);

    const load = async () => {
        try { setItems(asArray((await api.get("/admin/news")).data, "news")); } catch (e) { toast.error(formatApiError(e)); }
    };
    useEffect(() => { load(); }, []);

    const save = async () => {
        try {
            const payload = { ...editing };
            if (payload.published_at && payload.published_at.length === 10) {
                payload.published_at = new Date(payload.published_at).toISOString();
            }
            if (payload.id) await api.patch(`/admin/news/${payload.id}`, payload);
            else await api.post("/admin/news", payload);
            toast.success("Saved");
            setEditing(null);
            load();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const remove = async (id) => {
        if (!window.confirm("Delete?")) return;
        try { await api.delete(`/admin/news/${id}`); load(); } catch (e) { toast.error(formatApiError(e)); }
    };

    if (editing) {
        const e = editing;
        return (
            <div data-testid="admin-news-form">
                <button onClick={() => setEditing(null)} className="text-sm text-[#6B7280] mb-6 inline-flex items-center gap-2"><X size={14} /> Cancel</button>
                <h2 className="heading text-3xl tracking-tight mb-8">{e.id ? "Edit News" : "New Article"}</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 bg-white p-8">
                    <Field label="Slug"><input data-testid="news-slug" className={inputCls} value={e.slug} onChange={(ev) => setEditing({ ...e, slug: ev.target.value })} /></Field>
                    <Field label="Published date"><input type="date" className={inputCls} value={(e.published_at || "").slice(0, 10)} onChange={(ev) => setEditing({ ...e, published_at: ev.target.value })} /></Field>
                    <Field label="Title (AR)"><input data-testid="news-title-ar" className={inputCls} value={e.title_ar} onChange={(ev) => setEditing({ ...e, title_ar: ev.target.value })} /></Field>
                    <Field label="Title (EN)"><input data-testid="news-title-en" className={inputCls} value={e.title_en} onChange={(ev) => setEditing({ ...e, title_en: ev.target.value })} /></Field>
                    <Field label="Category (AR)"><input className={inputCls} value={e.category_ar} onChange={(ev) => setEditing({ ...e, category_ar: ev.target.value })} /></Field>
                    <Field label="Category (EN)"><input className={inputCls} value={e.category_en} onChange={(ev) => setEditing({ ...e, category_en: ev.target.value })} /></Field>
                    <div className="md:col-span-2"><FileUpload value={e.cover_image} onChange={(v) => setEditing({ ...e, cover_image: v })} label="Cover image" testid="news-cover-upload" /></div>
                    <Field label="Excerpt (AR)"><textarea rows={2} className={textareaCls} value={e.excerpt_ar} onChange={(ev) => setEditing({ ...e, excerpt_ar: ev.target.value })} /></Field>
                    <Field label="Excerpt (EN)"><textarea rows={2} className={textareaCls} value={e.excerpt_en} onChange={(ev) => setEditing({ ...e, excerpt_en: ev.target.value })} /></Field>
                    <Field label="Body (AR)"><textarea rows={6} className={textareaCls} value={e.body_ar} onChange={(ev) => setEditing({ ...e, body_ar: ev.target.value })} /></Field>
                    <Field label="Body (EN)"><textarea rows={6} className={textareaCls} value={e.body_en} onChange={(ev) => setEditing({ ...e, body_en: ev.target.value })} /></Field>
                    <label className="inline-flex items-center gap-2 text-sm md:col-span-2">
                        <input type="checkbox" checked={e.published} onChange={(ev) => setEditing({ ...e, published: ev.target.checked })} /> Published
                    </label>
                </div>
                <div className="mt-6 flex gap-3">
                    <button onClick={save} data-testid="news-save" className="bg-[#002951] hover:bg-[#0060A7] text-white px-8 py-3 text-sm uppercase tracking-[0.2em]">Save</button>
                    <button onClick={() => setEditing(null)} className="border border-black/15 px-8 py-3 text-sm uppercase tracking-[0.2em]">Cancel</button>
                </div>
            </div>
        );
    }

    return (
        <div data-testid="admin-news-page">
            <div className="flex items-center justify-between mb-6">
                <h1 className="heading text-3xl tracking-tight">News</h1>
                <button onClick={() => setEditing(blank())} data-testid="add-news-btn" className="bg-[#002951] hover:bg-[#0060A7] text-white px-5 py-2.5 text-sm inline-flex items-center gap-2">
                    <Plus size={14} /> New Article
                </button>
            </div>
            <div className="bg-white">
                <table className="w-full text-sm">
                    <thead className="bg-[#EFF0F1] text-left text-xs uppercase tracking-[0.2em] text-[#6B7280]">
                        <tr><th className="p-4">Title</th><th className="p-4">Slug</th><th className="p-4">Date</th><th className="p-4">Status</th><th className="p-4"></th></tr>
                    </thead>
                    <tbody>
                        {items.map((n) => (
                            <tr key={n.id} className="border-t border-black/5" data-testid={`news-row-${n.slug}`}>
                                <td className="p-4">
                                    <div className="heading text-base">{n.title_en}</div>
                                    <div className="text-xs text-[#6B7280]">{n.title_ar}</div>
                                </td>
                                <td className="p-4 text-[#6B7280]">{n.slug}</td>
                                <td className="p-4 text-[#6B7280]">{(n.published_at || "").slice(0, 10)}</td>
                                <td className="p-4">{n.published ? <span className="text-emerald-600 text-xs">Published</span> : <span className="text-[#6B7280] text-xs">Draft</span>}</td>
                                <td className="p-4 text-right">
                                    <button onClick={() => setEditing(n)} className="mr-3 text-[#0060A7]" data-testid={`edit-news-${n.slug}`}><Edit3 size={16} /></button>
                                    <button onClick={() => remove(n.id)} className="text-red-500" data-testid={`delete-news-${n.slug}`}><Trash2 size={16} /></button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
